// @flow
import React from 'react';
import type { Node } from 'react';
import getIn from '@mzvonar/getin';
import setIn from '@mzvonar/setin';
import type { Config as ConfigType } from '../../../../types';
import Config from '../../../../models/Config';
import BooleanComponent from './Boolean';
import Text from './Text';
import ArrayComponent from './Array';
import Select from './Select';
import Coordinate from './Coordinate';

export type Props = $ReadOnly<{
    type: 'boolean' | 'text' | 'number' | 'array' | 'select' | 'coordinate',
    label: Node,
    path: $ReadOnlyArray<string>,
    config: ConfigType,
    prepend?: Node,
    append?: Node,
    className?: string,
    classes?: {},

    inputFormatter?: (value: any) => number | string,
    outputFormatter?: (value: number | string) => any,
    onChange?: (path: $ReadOnlyArray<string>, value: any, config: ConfigType) => void
}>;

function ConfigItem({ type, label, path, config, prepend, append, className, classes, outputFormatter, onChange, ...rest }: Props) {
    const value = getIn(config, path);

    const handleChange = (path, value) => {
        const newValue = outputFormatter ? outputFormatter(value) : value;

        let newValueObject;
        if(path.length > 2) {
            const oldValue = getIn(config, path.slice(0, 2), {});
            newValueObject = {
                [path[1]]: setIn(oldValue, path.slice(2), newValue)
            };
        }
        else {
            newValueObject = {
                [path[1]]: newValue
            };
        }

        Config.findByIdAndUpdate(path[0], newValueObject, { upsert: true });

        if(onChange) {
            onChange(path, newValue, newValueObject);
        }
    };

    const { field: fieldClass, ...componentClasses } = classes || {};

    let component;
    if(type === 'boolean') {
        component = (
            <BooleanComponent
                {...rest}
                className={fieldClass}
                classes={componentClasses}
                label={label}
                path={path}
                value={value}
                onChange={handleChange}
            />
        );
    }
    else if(type === 'text' || type === 'number') {
        component = (
            <Text
                {...rest}
                className={fieldClass}
                classes={componentClasses}
                type={type}
                label={label}
                path={path}
                value={value}
                prepend={prepend}
                append={append}
                onChange={handleChange}
            />
        );
    }
    else if(type === 'array') {
        component = (
            <ArrayComponent
                {...rest}
                className={fieldClass}
                classes={componentClasses}
                label={label}
                path={path}
                value={value}
                onChange={handleChange}
                append={append}
            />
        );
    }
    else if(type === 'select') {
        component = (
            <Select
                {...rest}
                className={fieldClass}
                classes={componentClasses}
                label={label}
                path={path}
                value={value}
                onChange={handleChange}
            />
        );
    }
    else if(type === 'coordinate') {
        component = (
            <Coordinate
                {...rest}
                className={fieldClass}
                classes={componentClasses}
                label={label}
                path={path}
                value={value}
                onChange={handleChange}
            />
        );
    }
    else {
        throw new Error(`Unknown ConfigItem type ${type}`);
    }

    return (
        <div className={className}>
            {component}
        </div>
    );
}

export default ConfigItem;